// CartProvider — plan 23 / plan 84.
//
// The cart lives in a tiny context behind `useCart()`. We persist to
// `sessionStorage` so the cart survives a hard reload but NOT a new tab
// (plan 22 forbids sensitive state in `localStorage`; cart is per-session
// anyway). An invalid JSON blob on load is treated as an empty cart so
// a corrupt value doesn't brick the UI.
//
// Plan 84 — cart shape change:
//   - `cart: Service[]` → `cart: CartItem[]`. Each CartItem wraps a
//     Service plus an optional `childCount` (only meaningful for the
//     Parent/Child Session — defaults to 1 elsewhere).
//   - `add(service, opts?: { childCount? })` — second arg defaults to
//     `undefined` for non Parent/Child services.
//   - `setChildCount(serviceId, n)` — clamp [1, 4]; the Services card
//     and CartTray quantity selectors call this to live-update the line
//     total. The server recomputes the actual price from `child_count`
//     at checkout — the client number is for display only.
//   - The plan-81 Additional Child rules (`MAX_ADDITIONAL_CHILDREN`,
//     `isAdditionalChild`, cascade-remove) are gone — there is no
//     Additional Child entry in the cart anymore.
//
// Persistence: the storage key is bumped to `hd:cart:v2` so any
// in-flight legacy `Service[]`-shaped cart is dropped cleanly. Owner
// approved this on staging (no real prod data yet).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Service } from '../../shared/types';
import { isParentChildBase } from '../../shared/services/serviceTypes';
import { parentChildPrice } from '../../shared/services/parentChildPricing';
import { useCurrentUser } from '../auth/useCurrentUser';

/** sessionStorage key. Bump when the persisted shape changes. */
export const CART_STORAGE_KEY = 'hd:cart:v2';

/**
 * One cart entry. `childCount` is only meaningful for the Parent/Child
 * Session — every other service ignores it (defaults to 1 for total math).
 */
export interface CartItem {
  service: Service;
  /** Only meaningful for Parent/Child Session; defaults to 1. */
  childCount?: number;
}

/** Result of a cart `add()` — distinguishes "duplicate" from "ok". */
export type CartAddResult = { added: true } | { added: false; reason: 'duplicate' };

interface AddOpts {
  /** Only honoured for the Parent/Child Session; ignored elsewhere. */
  childCount?: number;
}

interface CartContextValue {
  cart: CartItem[];
  /**
   * Adds the service if not already in the cart; returns a discriminated
   * result. The optional `childCount` is only honoured for the Parent/Child
   * Session — every other service ignores it.
   */
  add: (service: Service, opts?: AddOpts) => CartAddResult;
  remove: (serviceId: number) => void;
  /**
   * Plan 84 — update the child count for an in-cart Parent/Child Session.
   * No-op if the matching item isn't a Parent/Child Session. Clamps the
   * input to [1, 4] (the Services card and CartTray pill UIs constrain
   * the value at the surface but we double-up here for safety).
   */
  setChildCount: (serviceId: number, childCount: number) => void;
  clear: () => void;
  count: number;
  total: number;
  isOpen: boolean;
  openTray: () => void;
  closeTray: () => void;
  toggleTray: () => void;
  /**
   * Latest user-facing notice from a constraint hit. Today this only
   * surfaces the duplicate-add case (cart-tray inline messaging). Kept
   * for the existing CartTray + Services banner wiring even though the
   * plan-81 Additional Child rules are gone.
   */
  notice: string | null;
  dismissNotice: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

/**
 * Read sessionStorage and narrow each entry into a CartItem. Anything
 * that doesn't fit the new shape gets dropped — including the legacy
 * plan-81 `Service[]` shape, since we bumped the storage key. The key
 * bump means we WILL drop in-flight legacy carts; owner approved on
 * staging (no real prod data yet).
 */
function loadInitial(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is CartItem => {
      if (!item || typeof item !== 'object') return false;
      const candidate = item as { service?: unknown; childCount?: unknown };
      const svc = candidate.service as Partial<Service> | undefined;
      if (!svc || typeof svc !== 'object') return false;
      return (
        typeof svc.id === 'number' && typeof svc.name === 'string' && typeof svc.price === 'number'
      );
    });
  } catch {
    return [];
  }
}

/**
 * Compute a single line item's total cents amount. The Parent/Child
 * Session uses the tier table; every other service is `service.price`.
 */
function lineTotal(item: CartItem): number {
  if (isParentChildBase(item.service)) {
    return parentChildPrice(item.childCount ?? 1);
  }
  return item.service.price;
}

function clampChildCount(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(4, Math.floor(n)));
}

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>(loadInitial);
  const [isOpen, setIsOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // We mirror `cart` into a ref so `add()` can return a synchronous
  // accept/reject decision without depending on whether React batched the
  // setState callback yet. The ref starts in lockstep with the state via
  // the effect below, AND `add()` advances the ref in the same tick when
  // it accepts a service — so back-to-back `add()` calls within a single
  // event-handler tick each see the post-previous-add state.
  const cartRef = useRef<CartItem[]>(cart);
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  // Persist every mutation. Failing to write sessionStorage (Safari
  // private mode etc.) must not crash the app — swallow and move on.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (err) {
      console.warn('Cart persist failed:', err);
    }
  }, [cart]);

  // Clear the cart on user identity change — but ONLY when the previous
  // identity was a logged-in user (sign-out, or switch to a different
  // user). The guest-to-logged-in transition (`null → 42`) is the common
  // "shopping while logged out, log in to check out" flow — wiping the
  // cart there silently dropped the items the user was about to buy and
  // landed them on the Schedule step of an empty BookingModal instead
  // of the Payment step.
  //
  // Truth table:
  //   prev=undefined (first render)        → never clear (cart still loading)
  //   prev=null,  next=null  (still guest) → never clear (no change)
  //   prev=null,  next=42    (login)       → never clear (preserve cart) ◆
  //   prev=42,    next=null  (logout)      → CLEAR (security)
  //   prev=42,    next=99    (switch user) → CLEAR (security)
  //
  // ◆ The diff vs. the prior implementation. On a shared device a logout
  //   already wipes the cart, so the next user starts from empty —
  //   preserving the guest cart on login doesn't open a leak.
  const { user: currentUser } = useCurrentUser();
  const prevUserIdRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevUserIdRef.current;
    const next = currentUser?.id ?? null;
    if (prev !== undefined && prev !== null && prev !== next) {
      setCart([]);
      setNotice(null);
      try {
        window.sessionStorage.removeItem(CART_STORAGE_KEY);
      } catch {
        /* private mode — in-memory clear above already covers this */
      }
    }
    prevUserIdRef.current = next;
  }, [currentUser?.id]);

  /**
   * Optimistic: updates local state synchronously and opens the tray on
   * a successful add. Duplicates are silently rejected (one entry per
   * service id; quantity changes go through `setChildCount`).
   */
  const add = useCallback<CartContextValue['add']>((service, opts) => {
    const prev = cartRef.current;
    if (prev.find((c) => c.service.id === service.id)) {
      setNotice(null);
      return { added: false, reason: 'duplicate' };
    }
    const childCount = isParentChildBase(service)
      ? clampChildCount(opts?.childCount ?? 1)
      : undefined;
    const item: CartItem = childCount !== undefined ? { service, childCount } : { service };
    const next = [...prev, item];
    cartRef.current = next;
    setCart(next);
    setIsOpen(true);
    setNotice(null);
    return { added: true };
  }, []);

  /**
   * Optimistic: updates local state synchronously. Removing a non-existent
   * id is a no-op.
   */
  const remove = useCallback<CartContextValue['remove']>((serviceId) => {
    const prev = cartRef.current;
    const next = prev.filter((c) => c.service.id !== serviceId);
    if (next.length === prev.length) return;
    cartRef.current = next;
    setCart(next);
  }, []);

  const setChildCount = useCallback<CartContextValue['setChildCount']>((serviceId, childCount) => {
    const prev = cartRef.current;
    const idx = prev.findIndex((c) => c.service.id === serviceId);
    if (idx === -1) return;
    const target = prev[idx]!;
    if (!isParentChildBase(target.service)) return;
    const clamped = clampChildCount(childCount);
    if ((target.childCount ?? 1) === clamped) return;
    const next = prev.slice();
    next[idx] = { ...target, childCount: clamped };
    cartRef.current = next;
    setCart(next);
  }, []);

  const clear = useCallback(() => {
    cartRef.current = [];
    setCart([]);
    setIsOpen(false);
    setNotice(null);
  }, []);

  const openTray = useCallback(() => setIsOpen(true), []);
  const closeTray = useCallback(() => setIsOpen(false), []);
  const toggleTray = useCallback(() => setIsOpen((v) => !v), []);
  const dismissNotice = useCallback(() => setNotice(null), []);

  const total = useMemo(() => cart.reduce((acc, c) => acc + lineTotal(c), 0), [cart]);

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      add,
      remove,
      setChildCount,
      clear,
      count: cart.length,
      total,
      isOpen,
      openTray,
      closeTray,
      toggleTray,
      notice,
      dismissNotice,
    }),
    [
      cart,
      add,
      remove,
      setChildCount,
      clear,
      total,
      isOpen,
      openTray,
      closeTray,
      toggleTray,
      notice,
      dismissNotice,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextValue => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
};
